/**
 * Portes de récompense posées sur les sorties d'une salle générée. Une salle a 1 ou 2 sorties et l'étage offre
 * 2 ou 3 portes : les portes sont réparties sur les sorties, côte à côte quand elles en partagent une, un peu en
 * retrait du bord (dans le couloir ou le chemin qui y mène), sur des positions marchables atteignables depuis le départ.
 */
import type { RoomDef } from '../../data/schema';
import type { Vec2 } from '../types';
import { clampToWalkable } from '../world';
import { bfs, cellAt } from './grid';
import { walkOf } from './nav';

/** Distance (px) entre les pieds du joueur et le centre d'une porte qui déclenche le passage. */
export const DOOR_TRIGGER = 80;
/** Rayon de l'empreinte d'une porte (place libre autour de son centre). */
const DOOR_RADIUS = 36;
/** Écart minimal entre deux portes : leurs zones de déclenchement ne se chevauchent pas. */
const DOOR_GAP = DOOR_TRIGGER * 2 + 60;
const INWARD = [300, 460, 620, 800];
const SPREAD = 300;

/** Positions des `count` portes de la salle, ou null si la salle n'a pas de sorties (salle à rectangles). */
export function placeDoors(room: RoomDef, count: number): Vec2[] | null {
  const exits = room.grid?.exits;
  if (!room.grid || !exits?.length || count <= 0) return null;
  const walk = walkOf(room.grid);
  const reach = bfs(walk, cellAt(room.playerStart.x, room.playerStart.y));
  const reachable = (p: Vec2): boolean => { const c = cellAt(p.x, p.y); return c >= 0 && reach[c]! >= 0; };
  const out: Vec2[] = [];
  const perExit = exits.map((_, e) => Array.from({ length: count }, (_, i) => i).filter((i) => i % exits.length === e).length);
  for (let i = 0; i < count; i++) {
    const e = i % exits.length;
    const exit = exits[e]!;
    const j = Math.floor(i / exits.length), m = perExit[e]!;
    // vers l'intérieur : de la sortie vers le départ du joueur (le chemin de la sortie y mène)
    const dx = room.playerStart.x - exit.x, dy = room.playerStart.y - exit.y, len = Math.hypot(dx, dy) || 1;
    const inx = dx / len, iny = dy / len, px = -iny, py = inx;
    const offsets = [(j - (m - 1) / 2) * SPREAD, ...(m > 1 ? [(j - (m - 1) / 2) * SPREAD * 0.5, 0] : [])];
    let best: Vec2 | null = null;
    for (const d of INWARD) {
      for (const o of offsets) {
        const q = clampToWalkable({ x: exit.x + inx * d + px * o, y: exit.y + iny * d + py * o }, DOOR_RADIUS, room);
        if (!reachable(q) || out.some((p) => Math.hypot(p.x - q.x, p.y - q.y) < DOOR_GAP)) continue;
        best = q;
        break;
      }
      if (best) break;
    }
    // repli : la première position marchable atteignable, même trop proche d'une autre porte
    out.push(best ?? clampToWalkable({ x: exit.x + inx * INWARD[0]!, y: exit.y + iny * INWARD[0]! }, DOOR_RADIUS, room));
  }
  return out;
}

/** Indice de la porte sous les pieds du joueur (distance <= DOOR_TRIGGER), ou -1. */
export function doorAt(doors: readonly { pos?: Vec2 }[], p: Vec2): number {
  let best = -1, bestD = DOOR_TRIGGER;
  doors.forEach((d, i) => {
    if (!d.pos) return;
    const dist = Math.hypot(d.pos.x - p.x, d.pos.y - p.y);
    if (dist <= bestD) { bestD = dist; best = i; }
  });
  return best;
}
